/**
 * @fileoverview A component that displays a profile block with a title and a table of content.
 * It uses the `react-tooltip` library to display tooltips.
 *
 * @example
 * <ProfileBlock title="Profile" content={[{label: "Name", content: <div>John Doe</div>, hint: "This is the name of the user."}]} />
 */

import { Tooltip } from 'react-tooltip'
import QuestionIcon from '../assets/question.svg'

/**
 * @interface ProfileBlockProps
 * @description Props for the ProfileBlock component.
 * @property {string} title - The title of the profile block.
 * @property {[string, JSX.Element, string?][]} content - The content of the profile block.
 */
interface ProfileBlockProps {
    title: string
    content: [string, JSX.Element, string?][]
}

/**
 * @function ProfileBlock
 * @description Component for displaying a profile block with a title and content.
 * @param {ProfileBlockProps} props - The props for the ProfileBlock component.
 * @returns {JSX.Element | null} - The ProfileBlock component.
 */
export function ProfileBlock(props: ProfileBlockProps) {
    return (
        <div>
            <h1 className="font-inter text-xl font-semibold leading-7 text-left mb-4">
                {props.title}
            </h1>
            <table className="table-fixed w-full">
                <tbody className="flex flex-col gap-y-1">
                    {props.content
                        .filter((item) => item)
                        .map(([label, content, hint], index) => (
                            <tr key={label} className="h-7">
                                <td className="w-40 h-7 text-sm text-gray-600">
                                    <span className="inline-flex gap-x-1">
                                        {label}
                                        {hint && (
                                            <div id={`tooltip-${index}`}>
                                                <QuestionIcon className="w-5 h-5 fill-gray-400 transition-colors cursor-pointer hover:fill-indigo-500" />
                                                <Tooltip anchorSelect={`#tooltip-${index}`}>
                                                    <p className="w-40 whitespace-normal text-xs font-normal">
                                                        {hint}
                                                    </p>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </span>
                                </td>
                                <td className="h-7 font-medium text-sm">{content}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    )
}
